import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { ChevronDownIcon, ArrowRightOnRectangleIcon, TrashIcon, UserGroupIcon } from '@heroicons/react/24/solid';
import { motion, AnimatePresence } from 'framer-motion';
import { useMutation } from '@tanstack/react-query';
import GraphqlService from '../service/graphqlService';
import { config } from '../config/awsExports';

const { env } = config;

function clearCognitoKeys() {
  // Eliminar username y userToken del localStorage
  localStorage.removeItem('username');

  // Iterate through all keys in localStorage
  Object.keys(localStorage).forEach((key) => {
    // Check if the key includes 'CognitoIdentityServiceProvider'
    if (key.includes('CognitoIdentityServiceProvider')) {
      localStorage.removeItem(key); // Remove the matching key
    }
  });
}

function CustomSignoutButton() {
  const signOut = async (e) => {
    e.preventDefault();
    clearCognitoKeys();
    window.location.reload();
  };

  return (
    <motion.button
      type="button"
      onClick={signOut}
      className="w-full rounded-xl mx-1.5 mb-3 h-12 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-medium py-2 px-4 transition-colors duration-300 flex items-center justify-center group cursor-pointer"
      whileHover={{ scale: 1.03 }}
      whileTap={{ scale: 0.97 }}
    >
      <span>Sign out</span>
      <ArrowRightOnRectangleIcon className="h-5 w-5 ml-2 group-hover:translate-x-1 transition-transform duration-300" />
    </motion.button>
  );
}

function VivaEngageButton() {
  const openVivaEngage = () => {
    window.open(env.vivaEngage, '_blank', 'noopener,noreferrer');
  };

  return (
    <motion.button
      type="button"
      onClick={openVivaEngage}
      className="w-full rounded-xl mx-1.5 mb-3 h-12 bg-white border-2 border-purple-500 hover:bg-purple-50 text-purple-600 font-medium py-2 px-4 transition-colors duration-300 flex items-center justify-center group cursor-pointer"
      whileHover={{ scale: 1.03 }}
      whileTap={{ scale: 0.97 }}
    >
      <UserGroupIcon className="h-5 w-5 mr-2 group-hover:scale-110 transition-transform duration-300" />
      <span>Viva Engage Community</span>
    </motion.button>
  );
}

function DeleteAccountLink({ onDeleteClick }) {
  return (
    <div className="w-full flex justify-center mx-1.5 mb-3 mt-6">
      <button
        type="button"
        onClick={onDeleteClick}
        className="text-m text-red-600 hover:text-red-700 transition-colors duration-200 cursor-pointer underline"
      >
        Delete Account
      </button>
    </div>
  );
}

DeleteAccountLink.propTypes = {
  onDeleteClick: PropTypes.func.isRequired,
};

function ConfirmDeleteModal({ isOpen, onClose, onConfirm, isDeleting }) {
  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className="bg-white rounded-2xl shadow-2xl p-6 max-w-md w-full mx-4"
            initial={{ scale: 0.9, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.9, y: 20 }}
          >
            <div className="flex items-center justify-center mb-4">
              <div className="bg-red-100 rounded-full p-3">
                <TrashIcon className="h-8 w-8 text-red-600" />
              </div>
            </div>
            <h2 className="text-xl font-bold text-center text-gray-900 mb-3">
              Delete Your Account?
            </h2>
            <p className="text-gray-700 text-center mb-6">
              Are you sure you want to delete your account? This action cannot be undone, and you will lose access to the Portal unless you request permissions again.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <motion.button
                type="button"
                onClick={onClose}
                disabled={isDeleting}
                className="flex-1 px-4 py-3 bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium rounded-xl transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Cancel
              </motion.button>
              <motion.button
                type="button"
                onClick={onConfirm}
                disabled={isDeleting}
                className="flex-1 px-4 py-3 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-medium rounded-xl transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {isDeleting ? 'Deleting...' : 'Delete Account'}
              </motion.button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

ConfirmDeleteModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  isDeleting: PropTypes.bool.isRequired,
};

function Profile({ user }) {
  const [, setAnchorEl] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const wrapperRef = useRef(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
    setIsOpen(!isOpen);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleOpenDeleteModal = () => {
    setIsDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
  };

  const { mutate: deleteAccount, isLoading: isDeleting } = useMutation({
    mutationFn: async () => {
      return GraphqlService.deleteMyAccount();
    },
    onSuccess: () => {
      clearCognitoKeys();
      window.location.reload();
    },
    onError: (error) => {
      console.error('Error deleting account:', error);
      alert(`Failed to delete account: ${error.message}`);
      setIsDeleteModalOpen(false);
    }
  });

  const handleConfirmDelete = () => {
    deleteAccount();
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }

    // Bind the event listener
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [wrapperRef]);

  // Get first letter of username for avatar
  const userInitial = user.substring(0, 1).toUpperCase();

  // Generate a background color based on the user's initial
  const colors = [
    'bg-blue-200 text-blue-700',
    'bg-green-200 text-green-700',
    'bg-purple-200 text-purple-700',
    'bg-amber-200 text-amber-700',
    'bg-rose-200 text-rose-700',
  ];

  const colorIndex = userInitial.charCodeAt(0) % colors.length;
  const avatarColor = colors[colorIndex];

  return (
    <div className="relative" ref={wrapperRef}>
      <motion.div
        className="flex items-center cursor-pointer bg-white/10 px-3 py-2 rounded-full hover:bg-white/20 transition-colors duration-300"
        onClick={handleClick}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
      >
        <motion.div
          className={`h-8 w-8 rounded-full ${avatarColor} flex items-center justify-center text-sm font-medium shadow-sm`}
          whileHover={{ rotate: [0, -10, 10, -5, 0] }}
          transition={{ duration: 0.5 }}
        >
          {userInitial}
        </motion.div>
        <span className="mx-2 text-base font-small" id="user-header-email">
          {user}
        </span>
        <motion.div
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.3 }}
        >
          <ChevronDownIcon className="h-5 w-5 text-gray-600" />
        </motion.div>
      </motion.div>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="absolute top-full mt-2 right-0 bg-white shadow-xl rounded-2xl py-4 z-10 w-72 max-h-80 overflow-y-auto tailwind-scrollbar scrollbar-thin scrollbar-thumb-blue-300 scrollbar-track-gray-100 border border-gray-100"
            initial={{ opacity: 0, y: -20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            onBlur={handleClose}
          >
            <div className="flex flex-col items-start px-2">

              <CustomSignoutButton />

              <VivaEngageButton />
              
              <DeleteAccountLink onDeleteClick={handleOpenDeleteModal} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <ConfirmDeleteModal
        isOpen={isDeleteModalOpen}
        onClose={handleCloseDeleteModal}
        onConfirm={handleConfirmDelete}
        isDeleting={isDeleting}
      />
    </div>
  );
}

Profile.propTypes = {
  user: PropTypes.string.isRequired,
};

export default Profile;
